import i18n from '@/plugins/i18n'
import { SIGN_IN_ROUTE_NAME } from '@/router/routerData'
import { getRouter } from '@/router/routerInstance'

export const AUTH_DATA_COOKIE_KEY = 'user-auth-data'

/**
 * Sends the user back to the signIn view
 * @param {object} params The params to send to the signIn view. By default, the locale passed is the current one.
 */
function backToSignIn (params = {}, query = {}) {
  getRouter().push(getSignInRoute(params, query))
}

/**
 * Get the signIn route with default params added
 * @param {object} params The params to send to the signIn view. By default, the locale passed is the current one.
 * @param {object} query The query parameters to send to the signIn view.
 * @returns {object} A route object to go to the signIn view
 */
function getSignInRoute (params = {}, query = {}) {
  params = {
    locale: i18n.global.locale.value,
    ...params,
  }
  return { name: SIGN_IN_ROUTE_NAME, params, query }
}

// A redirect target is usable only if it is a same-app path (but not a
// protocol-relative `//host` open redirect) or an explicit http(s) URL.
const SAFE_REDIRECT = /^\/(?!\/)|^https?:\/\//i

// Chars used to smuggle a scheme past a WAF / naive filter: all whitespace
// (\s, incl. unicode whitespace + BOM), C0/C1 control chars, DEL, and the
// zero-width chars ZWSP/ZWNJ/ZWJ (U+200B-U+200D).
// eslint-disable-next-line no-control-regex
const SMUGGLE_CHARS = new RegExp('[\\s\\u0000-\\u001f\\u007f\\u0080-\\u009f\\u200b-\\u200d]', 'g')

/**
 * Sanitizes the `intendedUrl` query param before it reaches window.location.replace.
 * Trims, NFKC-normalizes (folds fullwidth/homoglyph schemes like `ｊａｖａｓｃｒｉｐｔ:`),
 * removes whitespace/control/zero-width smuggling chars, then strips any script-bearing
 * scheme (javascript:/vbscript:/data:) wherever it appears. Only returns the result if
 * what remains is still a safe path or http(s) URL — otherwise null (caller falls back).
 * @param {string} url The raw intendedUrl query parameter.
 * @returns {string|null} The sanitized URL to redirect to, or null if none is safe.
 */
function sanitizeRedirectUrl (url) {
  if (typeof url !== 'string') return null
  const cleaned = url
    .trim()
    .normalize('NFKC')
    .replace(SMUGGLE_CHARS, '')
    .replace(/(?:javascript|vbscript|data):/gi, '')
  return SAFE_REDIRECT.test(cleaned) ? cleaned : null
}

export { backToSignIn, getSignInRoute, sanitizeRedirectUrl }
